import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import api from "../../../services/api";
import { endpoints } from "../../../services/endpoints";
import { getUserDisplayName, getUserIdOrEmail } from "../../../utils/user";

export default function CreateCompanies() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    name: "",
    taxId: "",
    contactMode: "none", // 'none' | 'existing' | 'new'
    contactUserId: null, // selected existing user id
    newContact: {
      name: "",
      identification: "",
    },
    linkContactToCompany: true, // asignar companyId al contacto tras crear
  });

  useEffect(() => {
    let alive = true;
    async function loadUsers() {
      try {
        setLoading(true);
        const res = await api.get(endpoints.users.list);
        const list = res.data?.data ?? res.data ?? [];
        if (alive) setUsers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadUsers();
    return () => { alive = false; };
  }, []);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${getUserDisplayName(u)} / ${getUserIdOrEmail(u) ?? ""} / ${u.company?.name ?? ""}`,
        companyId: u.companyId ?? u.company?.id ?? null,
      })),
    [users]
  );

  const selectedExistingUser = useMemo(
    () => userOptions.find((o) => o.value === form.contactUserId) || null,
    [userOptions, form.contactUserId]
  );

  const onInput = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onNewContactInput = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, newContact: { ...f.newContact, [name]: value } }));
  };

  const onSelectExistingUser = (opt) => {
    setForm((f) => ({ ...f, contactUserId: opt ? opt.value : null }));
  };

  const canSubmit = useMemo(() => {
    if (!form.name.trim() || !form.taxId.trim()) return false;
    if (form.contactMode === "existing" && !form.contactUserId) return false;
    if (form.contactMode === "new" && !form.newContact.name.trim()) return false;
    return !saving;
  }, [form, saving]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setErr(null);

    try {
      let contactUserId = null;

      // 1) Crear contacto si procede
      if (form.contactMode === "new") {
        const payloadUser = {
          name: form.newContact.name.trim(),
          identification: form.newContact.identification?.trim() || undefined,
        };
        const createUserRes = await api.post(endpoints.users.create, payloadUser);
        const userCreated = createUserRes.data?.data ?? createUserRes.data;
        contactUserId = userCreated?.id ?? null;
      }

      if (form.contactMode === "existing") {
        contactUserId = form.contactUserId;
      }

      // 2) Crear compañía
      const companyPayload = {
        name: form.name.trim(),
        taxId: form.taxId.trim(),
        ...(contactUserId ? { contactUserId } : {}),
      };

      const createCompanyRes = await api.post(endpoints.companies.create, companyPayload);
      const company = createCompanyRes.data?.data ?? createCompanyRes.data;

      // 3) (Opcional) vincular usuario a la compañía recién creada
      if (form.linkContactToCompany && contactUserId && company?.id) {
        try {
          await api.patch(endpoints.users.update(contactUserId), {
            companyId: company.id,
          });
        } catch (linkErr) {
          console.warn("No se pudo asignar el contacto a la compañía:", linkErr);
        }
      }

      navigate("/dashboardclientesadmin");
    } catch (e) {
      console.error(e);
      setErr(e);
      const msg =
        e?.response?.status === 409
          ? "Nombre o NIF ya existen (409)."
          : e?.response?.data?.message || "No se pudo crear la compañía.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Cargando…</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Crear compañía</h1>

      {err && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
          {String(err)}
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white p-6 rounded shadow space-y-6">
        {/* Datos básicos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium">Nombre</label>
            <input
              name="name"
              value={form.name}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
          </div>

          <div>
            <label className="block font-medium">NIF/CIF (taxId)</label>
            <input
              name="taxId"
              value={form.taxId}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
          </div>
        </div>

        {/* Contacto principal */}
        <div className="space-y-3">
          <div className="font-medium">Contacto principal</div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="contactMode"
                value="none"
                checked={form.contactMode === "none"}
                onChange={(e) => setForm((f) => ({ ...f, contactMode: e.target.value }))}
              />
              <span>Sin contacto por ahora</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="contactMode"
                value="existing"
                checked={form.contactMode === "existing"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, contactMode: e.target.value }))
                }
              />
              <span>Usar usuario existente</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="contactMode"
                value="new"
                checked={form.contactMode === "new"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, contactMode: e.target.value }))
                }
              />
              <span>Crear nuevo contacto</span>
            </label>
          </div>

          {/* Existing user */}
          {form.contactMode === "existing" && (
            <div className="space-y-2">
              <label className="block text-sm text-gray-600">Selecciona usuario</label>
              <Select
                options={userOptions}
                value={selectedExistingUser}
                onChange={onSelectExistingUser}
                isClearable
                isSearchable
                placeholder="Buscar usuario…"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.linkContactToCompany}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, linkContactToCompany: e.target.checked }))
                  }
                />
                Asignar este usuario a la nueva compañía
              </label>
            </div>
          )}

          {/* New contact */}
          {form.contactMode === "new" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Nombre del contacto</label>
                <input
                  name="name"
                  value={form.newContact.name}
                  onChange={onNewContactInput}
                  className="border rounded w-full p-2"
                  required
                />
              </div>
              <div>
                <label className="block font-medium">Identificación (opcional)</label>
                <input
                  name="identification"
                  value={form.newContact.identification}
                  onChange={onNewContactInput}
                  className="border rounded w-full p-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.linkContactToCompany}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, linkContactToCompany: e.target.checked }))
                    }
                  />
                  Asignar el contacto a la nueva compañía
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded border"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
            disabled={!canSubmit}
          >
            {saving ? "Creando…" : "Crear compañía"}
          </button>
        </div>
      </form>
    </div>
  );
}